import {useQueryClient} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useConfirmedMutation, type UseConfirmedMutationResult} from '@/hooks/useConfirmedMutation'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {useToast} from '@/hooks/use-toast'
import type {PartyRelationshipSchemaEntry} from '@/types/party'

const RELATIONSHIP_SCHEMA_KEY = 'relationship-schema'
const VIOLATIONS_KEY = 'violations'
const ENFORCEMENT_MODE_KEY = 'enforcement-mode'

export interface CreateEntryVariables {
    fromPartyType: string
    relationshipType: string
    toPartyType: string
    maxFromCount: number
    maxToCount: number
    fromSideDisplay: boolean
    toSideDisplay: boolean
    fromSideTree: boolean
    toSideTree: boolean
    fromSideLabel: string | null
    toSideLabel: string | null
    iconFrom: string | null
    iconTo: string | null
    aliasNameFrom: string | null
    aliasNameTo: string | null
    sortOrder: number
}

export interface UpdateEntryVariables extends CreateEntryVariables {
    id: string
    active: boolean
    version: number
}

export interface UseRelationshipSchemaMutationsOptions {
    onCreateSuccess?: (entry: PartyRelationshipSchemaEntry) => void
    onCreateError?: (err: unknown) => void
    onUpdateSuccess?: (entry: PartyRelationshipSchemaEntry) => void
    onDeactivateSuccess?: () => void
    onRunValidationSuccess?: () => void
    onUpdateEnforcementModeSuccess?: () => void
}

export interface RelationshipSchemaMutations {
    createEntry: ReturnType<typeof useEntityMutation<PartyRelationshipSchemaEntry, Error, CreateEntryVariables>>
    updateEntry: UseConfirmedMutationResult<PartyRelationshipSchemaEntry, UpdateEntryVariables>
    deactivateEntry: ReturnType<typeof useEntityMutation<PartyRelationshipSchemaEntry, Error, string>>
    runValidation: ReturnType<typeof useEntityMutation<unknown, Error, string>>
    updateEnforcementMode: ReturnType<typeof useEntityMutation<unknown, Error, string>>
}

export function useRelationshipSchemaMutations(
    options: UseRelationshipSchemaMutationsOptions = {},
): RelationshipSchemaMutations {
    const services = useServices()
    const i18n = useI18n()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const {
        onCreateSuccess,
        onCreateError,
        onUpdateSuccess,
        onDeactivateSuccess,
        onRunValidationSuccess,
        onUpdateEnforcementModeSuccess,
    } = options

    const createEntry = useEntityMutation<PartyRelationshipSchemaEntry, Error, CreateEntryVariables>({
        mutationFn: (vars) => services.partyRelationshipSchema.create(
            vars as unknown as Record<string, unknown>,
        ) as Promise<PartyRelationshipSchemaEntry>,
        invalidate: [RELATIONSHIP_SCHEMA_KEY],
        successMessage: i18n.partyRelationshipSchema.messages.createSuccess,
        onSuccess: (entry) => {
            onCreateSuccess?.(entry)
        },
        onError: (err) => {
            onCreateError?.(err)
        },
    })

    const updateEntry = useConfirmedMutation<PartyRelationshipSchemaEntry, UpdateEntryVariables>({
        mutationFn: (vars) => {
            const {id, ...rest} = vars
            return services.partyRelationshipSchema.update(
                id,
                rest as unknown as Record<string, unknown>,
            ) as Promise<PartyRelationshipSchemaEntry>
        },
        onSuccess: (entry) => {
            queryClient.invalidateQueries({queryKey: [RELATIONSHIP_SCHEMA_KEY]})
            toast({description: i18n.partyRelationshipSchema.messages.updateSuccess})
            onUpdateSuccess?.(entry)
        },
    })

    const deactivateEntry = useEntityMutation<PartyRelationshipSchemaEntry, Error, string>({
        mutationFn: (id) =>
            services.partyRelationshipSchema.deactivate(id) as Promise<PartyRelationshipSchemaEntry>,
        invalidate: [RELATIONSHIP_SCHEMA_KEY],
        successMessage: i18n.partyRelationshipSchema.messages.deactivateSuccess,
        onSuccess: () => {
            onDeactivateSuccess?.()
        },
    })

    const runValidation = useEntityMutation<unknown, Error, string>({
        mutationFn: (jobId) => services.jobs.trigger(jobId),
        successMessage: i18n.partyRelationshipSchema.violations.validationStarted,
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: [RELATIONSHIP_SCHEMA_KEY, VIOLATIONS_KEY]})
            queryClient.invalidateQueries({queryKey: ['relationship-schema-violations-count']})
            onRunValidationSuccess?.()
        },
    })

    const updateEnforcementMode = useEntityMutation<unknown, Error, string>({
        mutationFn: (mode) =>
            services.partyRelationshipSchema.updateEnforcementMode({mode}),
        invalidate: [[RELATIONSHIP_SCHEMA_KEY, ENFORCEMENT_MODE_KEY]],
        successMessage: i18n.partyRelationshipSchema.enforcement.updated,
        onSuccess: () => {
            onUpdateEnforcementModeSuccess?.()
        },
    })

    return {createEntry, updateEntry, deactivateEntry, runValidation, updateEnforcementMode}
}
